import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { registerUser } from "../api/registerUser";
import { useCountryOptions } from "../utils/countries";
import type { UserGroupSummary } from "../types/user";
import Checkbox from "@mui/material/Checkbox";
import { useAuth } from "../context/AuthContext";
import PersonIcon from "@mui/icons-material/Person";

type ExistingUser = {
  id: string;
  username: string;
  email: string;
};

type UserAddDialogProps = {
  open: boolean;
  onClose: () => void;
  resolveApiPath: (value: string) => string;
  existingUsers: ExistingUser[];
  availableUserGroups: UserGroupSummary[];
  onSuccess?: (result: { message: string }) => void;
};

type FormValues = {
  username: string;
  email: string;
  name: string;
  surname: string;
  company: string;
  password: string;
  confirmPassword: string;
  role: string;
  active: boolean;
  country: string;
  userGroups: string[];
};

const UserAddDialog = ({ open, onClose, resolveApiPath, existingUsers, availableUserGroups, onSuccess }: UserAddDialogProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const countryOptions = useCountryOptions();
  const { authorizedFetch } = useAuth();
  const userGroupMap = useMemo(() => {
    const entries = availableUserGroups.map((group) => [group.id, group.name] as const);
    return new Map(entries);
  }, [availableUserGroups]);

  const handleDialogClose = (_event?: unknown, reason?: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        username: Yup.string()
          .trim()
          .required("Username is required")
          .test("unique-username", "Username already exists", (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.trim().toLowerCase();
            return !existingUsers.some((candidate) => candidate.username.trim().toLowerCase() === normalized);
          }),
        email: Yup.string()
          .trim()
          .email("Enter a valid email")
          .required("Email is required")
          .test("unique-email", "Email already exists", (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.trim().toLowerCase();
            return !existingUsers.some((candidate) => candidate.email.trim().toLowerCase() === normalized);
          }),
        password: Yup.string().required("Password is required").min(4, "Password must be at least 4 characters"),
        confirmPassword: Yup.string()
          .required("Confirm your password")
          .oneOf([Yup.ref("password")], "Passwords do not match"),
        role: Yup.string()
          .trim()
          .required("Role is required")
          .oneOf(["admin", "user"], "Invalid role"),
        country: Yup.string()
          .trim()
          .test("country-length", "Country must be a 2-letter code", (value) => {
            if (!value) return true;
            const trimmed = value.trim();
            return trimmed.length === 2;
          })
        ,
      }),
    [existingUsers]
  );

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <PersonIcon sx={{ color: "text.secondary" }} />
          Add user
        </Box>
      </DialogTitle>
      <Formik<FormValues>
        enableReinitialize={false}
        initialValues={{
          username: "",
          email: "",
          name: "",
          surname: "",
          company: "",
          country: "",
          password: "",
          confirmPassword: "",
          role: "user",
          active: true,
          userGroups: []
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, helpers) => {
          helpers.setStatus(undefined);
          const trimmed = {
            username: values.username.trim(),
            email: values.email.trim(),
            name: values.name.trim(),
            surname: values.surname.trim(),
            company: values.company.trim(),
            country: values.country.trim().toUpperCase(),
            password: values.password,
            role: values.role.trim().toLowerCase() === "admin" ? "admin" : "user",
            active: values.active,
            userGroups: Array.from(
              new Set(
                values.userGroups
                  .map((group) => group.trim())
                  .filter((group) => group.length > 0)
              )
            )
          };

          try {
            const result = await registerUser(resolveApiPath, trimmed);
            if (!result.ok) {
              helpers.setStatus(result.error);
              return;
            }

            const registeredUser = result.user;
            if (registeredUser && trimmed.role !== (registeredUser.role ?? "").toLowerCase()) {
              const response = await authorizedFetch(`/api/users/${registeredUser.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  username: trimmed.username,
                  email: trimmed.email,
                  name: trimmed.name,
                  surname: trimmed.surname,
                  company: trimmed.company,
                  country: trimmed.country,
                  role: trimmed.role,
                  active: trimmed.active,
                  userGroups: trimmed.userGroups
                })
              });

              if (!response.ok) {
                const payload = await response.json().catch(() => null);
                const message = typeof payload?.error === "string" ? payload.error : "Unable to set role.";
                helpers.setStatus(message);
                return;
              }
            }

            onSuccess?.({ message: `${trimmed.username} was added.` });
            helpers.resetForm();
            setShowPassword(false);
            setShowConfirm(false);
            onClose();
          } catch (error) {
            console.error(error);
            helpers.setStatus("Unable to add user. Please try again.");
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,
          isSubmitting,
          isValid,
          dirty,
          status,
          setFieldValue
        }) => (
          <Form noValidate>
            <DialogContent>
              <Stack spacing={2}>
                {status ? <Alert severity="error">{status}</Alert> : null}
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    name="username"
                    label="Username"
                    fullWidth
                    value={values.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.username && Boolean(errors.username)}
                    helperText={touched.username ? errors.username : undefined}
                    autoFocus
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        name="active"
                        checked={values.active}
                        onChange={(_event, checked) => setFieldValue("active", checked)}
                        color="primary"
                      />
                    }
                    label={values.active ? "Active" : "Disabled"}
                    sx={{ m: 0, flexShrink: 0 }}
                  />
                </Stack>
                <TextField
                  name="email"
                  label="Email"
                  fullWidth
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email ? errors.email : undefined}
                  type="email"
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    name="name"
                    label="Name"
                    fullWidth
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <TextField
                    name="surname"
                    label="Surname"
                    fullWidth
                    value={values.surname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    name="company"
                    label="Company"
                    fullWidth
                    sx={{ flex: 1 }}
                    value={values.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <Autocomplete
                    fullWidth
                    sx={{ flexBasis: { xs: "100%", sm: "40%" } }}
                    options={countryOptions}
                    value={countryOptions.find((option) => option.code === values.country.trim().toUpperCase()) ?? null}
                    onChange={(_event, option) => setFieldValue("country", option?.code ?? "")}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    renderOption={(props, option) => (
                      <li {...props} key={option.code}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {option.Flag ? <option.Flag width={22} /> : null}
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        placeholder="Select country"
                        onBlur={handleBlur}
                        error={touched.country && Boolean(errors.country)}
                        helperText={touched.country ? errors.country : undefined}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: params.InputProps.startAdornment || (params.inputProps.value
                            ? (() => {
                                const selected = countryOptions.find(
                                  (option) => option.code === values.country.trim().toUpperCase()
                                );
                                return selected?.Flag ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
                                    <selected.Flag width={20} />
                                  </Box>
                                ) : null;
                              })()
                            : params.InputProps.startAdornment)
                        }}
                      />
                    )}
                    clearOnBlur
                  />
                </Stack>
                <FormControl size="small" sx={{ minWidth: 160, alignSelf: "flex-start" }}>
                  <InputLabel id="user-role-label">Role</InputLabel>
                  <Select
                    labelId="user-role-label"
                    label="Role"
                    name="role"
                    value={values.role}
                    onChange={(event) => setFieldValue("role", event.target.value)}
                    onBlur={handleBlur}
                    error={touched.role && Boolean(errors.role)}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                  {touched.role && errors.role ? <FormHelperText error>{errors.role}</FormHelperText> : null}
                </FormControl>
                <FormControl fullWidth size="small" disabled={availableUserGroups.length === 0}>
                  <InputLabel id="user-groups-label">User groups</InputLabel>
                  <Select
                    multiple
                    labelId="user-groups-label"
                    label="User groups"
                    name="userGroups"
                    value={values.userGroups}
                    onChange={(event) => {
                      const raw = event.target.value;
                      const next = Array.isArray(raw) ? raw : [raw];
                      setFieldValue(
                        "userGroups",
                        Array.from(
                          new Set(
                            next
                              .map((group) => String(group).trim())
                              .filter((group) => group.length > 0)
                          )
                        )
                      );
                    }}
                    renderValue={(selected) => {
                      if (!selected.length) {
                        return "None";
                      }
                      return selected
                        .map((id) => userGroupMap.get(String(id)) ?? String(id))
                        .join(", ");
                    }}
                  >
                    {availableUserGroups.length === 0 ? (
                      <MenuItem disabled value="__none">
                        No user groups
                      </MenuItem>
                    ) : (
                      availableUserGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          <Checkbox checked={values.userGroups.includes(group.id)} />
                          <ListItemText primary={group.name} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {availableUserGroups.length === 0 ? (
                    <FormHelperText>No user groups available.</FormHelperText>
                  ) : null}
                </FormControl>
                <TextField
                  name="password"
                  label="Password"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password ? errors.password : undefined}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  name="confirmPassword"
                  label="Confirm password"
                  fullWidth
                  type={showConfirm ? "text" : "password"}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword ? errors.confirmPassword : undefined}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  resetForm();
                  setShowPassword(false);
                  setShowConfirm(false);
                  onClose();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || !dirty}>
                Add user
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserAddDialog;
